/** @jsx jsx */
import { jsx } from '@emotion/core'
import { observer } from 'mobx-react'
import React, { Fragment, useEffect, useRef, useState } from 'react'
import { formatNumber, showToast, TOAST_TYPE } from '@utils/utils'
import { isMobile } from 'react-device-detect'
import useRootStore from '@store/useRootStore'
import _ from 'lodash'
import { BET_MONEY_TYPE } from 'adv-protocol/src/GameCommon'
import { FriendInvitationRewardReceivedStatus } from '@interface/common'
// img
import { Button } from '@styles/button.style'
import { DiaIco, GmoneyIco } from '@shared/Icon/MoneyIcon'
// css
import { RewardListWrap } from '../index.style'
import { RewardListContainer } from '@views/FriendsInvitation/index.style'
import classNames from 'classnames'
import { EMPTY_ICON } from '@constants/index'
import { AdvEmpty } from '@components/AdvEmpty'

enum FRIENDS_INVITATION_TAB_KEY {
  G_MONEY = 'gmoney',
  DIA = 'dia',
}
interface FriendInvitationRewardReceivedStatusExt extends FriendInvitationRewardReceivedStatus {
  percent: number
}
interface RewardListProps {
  currentTabKey: FRIENDS_INVITATION_TAB_KEY
}

const RewardList: React.FC<RewardListProps> = (props) => {
  const { componentStore } = useRootStore()
  const { friendInvitationInvitor } = componentStore
  const { currentTabKey } = props

  const [rewards, setRewards] = useState([])
  const [bettingMoneySummary, setBettingMoneySummary] = useState(0)
  const rewardListRef = useRef(null)
  const rewardListWrapRef = useRef(null)

  useEffect(() => {
    if (_.isEmpty(friendInvitationInvitor)) {
      return
    }

    const summary = _.toNumber(
      _.find(friendInvitationInvitor?.invitedUsersBettingMoneySummary, targetObject => {
        return _.toNumber(targetObject?.betMoneyType) === _.toNumber(friendInvitationInvitor.betMoneyType)
      })?.membershipClubBettingMoney || 0
    )
    setBettingMoneySummary(summary)
    setRewards(makeRewards(friendInvitationInvitor.rewardReceivedStates, summary))
  }, [friendInvitationInvitor])

  const makeRewards = (statuses: FriendInvitationRewardReceivedStatus[], paramSummary: number) => {
    const result: FriendInvitationRewardReceivedStatusExt[] = []
    let beforeStandard = 0
    _.forEach(statuses, status => {
      result.push({
        ...status,
        percent: calculateSectionPercent(beforeStandard, status.standard, paramSummary),
      })

      beforeStandard = status.standard
    })
    return result
  }

  const calculateSectionPercent = (beforeStandard: number, currentStandard: number, paramSummary: number) => {
    let percent: number
    if (currentStandard <= paramSummary) {
      percent = 100
    } else {
      const sectionValue = paramSummary - beforeStandard
      const range = currentStandard - beforeStandard
      percent = (sectionValue / range) * 100 + 0.01
      if (percent > 100) {
        percent = 100
      } else if (percent < 0) {
        percent = 0
      }
    }
    return percent
  }

  const validateRewardReceive = (reward: FriendInvitationRewardReceivedStatusExt) => {
    if (reward.isReceived) {
      return false
    }

    if (bettingMoneySummary < reward.standard) {
      return false
    }

    return true
  }

  const onClickRewardReceive = async (reward: FriendInvitationRewardReceivedStatusExt) => {
    if (!validateRewardReceive(reward)) {
      return
    }

    try {
      const result = await componentStore.api.component.receiveFriendInvitationReward({
        rewardId: reward.id,
      })
      if (result.result) {
        showToast(TOAST_TYPE.INFO, '보상이 메시지함으로 지급되었습니다.')
        setIsReceivedInRewards([reward.id], true)
      } else {
        showToast(TOAST_TYPE.ERROR, '보상 수령에 실패하였습니다. 새로고침 후 다시 시도해 주세요.')
      }
    } catch (err) {
      showToast(TOAST_TYPE.ERROR, '보상 수령에 실패하였습니다. 새로고침 후 다시 시도해 주세요.')
      console.error(err)
    }
  }

  const onClickAllRewardReceive = async () => {
    try {
      const result = await componentStore.api.component.receiveAllFriendInvitationReward({
        betMoneyType: friendInvitationInvitor.betMoneyType,
        startOfMonth: friendInvitationInvitor.startOfMonth,
      })

      if (!_.isEmpty(result.completeIds)) {
        showToast(TOAST_TYPE.INFO, '보상이 메시지함으로 지급되었습니다.')
        setIsReceivedInRewards(result.completeIds, true)
      } else {
        showToast(TOAST_TYPE.ERROR, '보상 수령에 실패하였습니다. 새로고침 후 다시 시도해 주세요.')
      }
    } catch (err) {
      showToast(TOAST_TYPE.ERROR, '보상 수령에 실패하였습니다. 새로고침 후 다시 시도해 주세요.')
      console.error(err)
    }
  }

  const setIsReceivedInRewards = (rewardIds: string[], isReceived: boolean) => {
    const cloneRewards = _.cloneDeep(rewards)
    setRewards([])
    for (const rewardId of rewardIds) {
      const foundReward = _.find(cloneRewards, reward => reward.id === rewardId)
      foundReward.isReceived = isReceived
    }
    setRewards(cloneRewards)
  }

  useEffect(() => {
    const wrapper = rewardListWrapRef.current
    const list = rewardListRef.current
    if (!wrapper || !list) return

    const incompleteItem = list.querySelector('li.reward-list--item.arrival:not(.complete)')
    const completeItems = list.querySelectorAll('li.reward-list--item.arrival.complete')
    const firstTarget = incompleteItem || completeItems[completeItems.length - 1]
    if (!firstTarget) {
      wrapper.scrollTo(0, 0)
      return
    }

    const wrapperRect = wrapper.getBoundingClientRect()
    const targetRect = firstTarget.getBoundingClientRect()

    const targetOffset = targetRect.left - wrapperRect.left + wrapper.scrollLeft
    const scrollX = targetOffset - wrapper.clientWidth / 2 + targetRect.width / 2 + 50
    wrapper.scrollTo(scrollX, 0)
  }, [rewardListRef, rewardListWrapRef, currentTabKey, rewards])

  const rewardAllBtnClassName = classNames('reward-all-btn', {
    pc: !isMobile
  })

  const sliceRewardName = (rewardName: string) => {
    if (rewardName.includes(',')) {
      return rewardName.slice(0, 7)
    }
    return rewardName.slice(0, 6)
  }

  const wrapStandardName = (standardName: string) => {
    const chunks = standardName.match(/.{1,5}/g) || []
    return chunks.map((chunk: string, idx: number) => (
      <React.Fragment key={idx}>
        {chunk}
        {idx !== chunks.length - 1 && <br />}
      </React.Fragment>
    ))
  }

  const receivable = _.some(
    rewards,
    reward => !reward.isReceived && bettingMoneySummary >= reward.standard,
  )

  const rewardListWidth = isMobile ? `calc(${rewards.length} * 103px + 24px)` : `calc(${rewards.length} * 103px - 16px`

  return (
    <RewardListContainer>
      {rewards.length === 0 ? (
          <AdvEmpty ico={EMPTY_ICON.REWARD_LIST}/>
        ) : (
          <Fragment>
            <RewardListWrap className={isMobile && 'm-reward-list-wrap'} ref={rewardListWrapRef}>
              <ul className="reward-list" key={currentTabKey} ref={rewardListRef} style={{ width: rewardListWidth }}>
                {rewards.map((reward) => {
                    const key = `FriendsInvitation:RewardList:${reward.standard}`
                    return (
                      <li key={key}
                          className={`reward-list--item ${reward.standard <= bettingMoneySummary && 'arrival'} ${reward.isReceived && 'complete'}`}>
                        <span className="range">{wrapStandardName(reward.standardName)}</span>

                        {/* 구간별 보상 영역 */}
                        <Button
                          className="reward-box"
                          onClick={() => {
                            onClickRewardReceive(reward)
                          }}
                        >
                          {sliceRewardName(reward.rewardName)}
                          <span className="reward-btn">수령</span>
                          <span className="check-ico"/>
                        </Button>

                        {/* 게이지 바 및 나의 현재 위치 */}
                        <span className="gauge-bar" style={{ width: `${reward.percent}%` }}>
                          {reward.percent > 0 &&
                            <div className="my-gauge">
                              {formatNumber(bettingMoneySummary)}
                              {_.toNumber(friendInvitationInvitor?.betMoneyType) === BET_MONEY_TYPE.G_MONEY
                                ? <GmoneyIco/> : <DiaIco/>}
                              <p className="gauge-txt">친구들의 베팅 총액</p>
                            </div>
                          }
                        </span>

                        {/* 금액 구간 달성 시 연출 */}
                        <span className="circle">
                          <span className="pulse-ani"/>
                          <span className="pulse-ani ani-2"/>
                          <span className="pulse-ani ani-3"/>
                        </span>
                      </li>
                    )
                  },
                )}
              </ul>
            </RewardListWrap>
            <Button className={rewardAllBtnClassName} disabled={!receivable} onClick={onClickAllRewardReceive}>모두
              수령</Button>
          </Fragment>
        )
      }
    </RewardListContainer>
  )
}

export default observer(RewardList)
